export type ProductStore = "Shein" | "Shopee" | "AliExpress" | "Amazon" | "Mercado Livre";

export type CatalogProductPayload = {
  id: string | number;
  nome: string;
  precoAtual: number;
  precoOriginal?: number;
  desconto?: number;
  avaliacao?: number;
  quantidadeAvaliacoes?: string | number;
  imagem: string;
  categoria?: string;
  subcategoria?: string;
  loja?: string;
  marca?: string;   // preferido
  brand?: string;   // fallback — campo legado do backend
  linkAfiliado: string;
  maisVendido?: boolean;
  destaque?: boolean;
  vendidos?: number;
};

export type CatalogPayload = {
  generatedAt?: string;
  timezone?: string;
  source?: string;
  count?: number;
  products?: CatalogProductPayload[];
};

export type RecentVideoPayloadItem = {
  assetId: number;
  source: string;
  videoId: number;
  postId?: string | null;
  postedAt?: string | null;
  title: string;
  affiliateLink: string;
  productUrl?: string | null;
  videoUrl: string;
  thumbnailUrl?: string | null;
  currentPrice?: number | null;
  originalPrice?: number | null;
  discountPercent?: number | null;
  rating?: number | null;
  group?: string | null;
};

export type RecentVideoPayload = {
  generatedAt?: string;
  count?: number;
  items?: RecentVideoPayloadItem[];
};

export type Product = {
  id: number;
  nome: string;
  precoAtual: number;
  precoOriginal: number;
  desconto: number;
  avaliacao: number;
  quantidadeAvaliacoes: string;
  imagem: string;
  categoria: string;
  loja: ProductStore;
  marca: string;
  linkAfiliado: string;
  vendidos: number;
  maisVendido?: boolean;
  destaque?: boolean;
};

export type RecentVideo = {
  assetId: number;
  source: string;
  videoId: number;
  postId?: string | null;
  postedAt?: string | null;
  title: string;
  affiliateLink: string;
  productUrl?: string | null;
  videoUrl: string;
  thumbnailUrl?: string | null;
  currentPrice: number | null;
  originalPrice: number | null;
  discountPercent: number;
  rating: number | null;
  group: string;
};

export const DEFAULT_CATEGORIES = [
  "Moda Feminina",
  "Blusas",
  "Calcas",
  "Shorts",
  "Vestidos",
  "Conjuntos",
  "Roupas",
  "Promocoes",
] as const;

export const DEFAULT_STORES = ["Shein"] as const;

export const EMPTY_PRODUCTS: Product[] = [];
export const EMPTY_RECENT_VIDEOS: RecentVideo[] = [];

function toStore(value?: string): ProductStore {
  const normalized = (value || "shein").trim().toLowerCase();
  switch (normalized) {
    case "shopee":
      return "Shopee";
    case "aliexpress":
      return "AliExpress";
    case "amazon":
      return "Amazon";
    case "mercado livre":
    case "mercadolivre":
      return "Mercado Livre";
    default:
      return "Shein";
  }
}

function normalizeMarca(product: CatalogProductPayload): string {
  // Lê `marca` (novo) ou `brand` (legado — catálogos gerados antes da correção)
  const raw = (product.marca || product.brand || "")
    .replace(/ /g, " ")  // non-breaking space → regular space
    .trim();
  return raw ? titleCase(raw) : "";
}

function titleCase(value: string): string {
  return value
    .replace(/[ _-]+/g, " ")  // non-breaking spaces, underscores, hyphens → space
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function toCategoryName(product: CatalogProductPayload): string {
  const base = product.categoria || product.subcategoria || "Moda Feminina";
  return titleCase(base);
}

function toReviewCount(value?: string | number): string {
  if (typeof value === "number") {
    return value <= 0 ? "Novo" : value.toLocaleString("pt-BR");
  }
  if (!value) {
    return "Novo";
  }
  return String(value);
}

function computeOriginalPrice(product: CatalogProductPayload): number {
  if (typeof product.precoOriginal === "number" && product.precoOriginal >= product.precoAtual) {
    return product.precoOriginal;
  }
  const discount = typeof product.desconto === "number" ? product.desconto : 0;
  if (discount > 0 && discount < 100) {
    return Number((product.precoAtual / (1 - discount / 100)).toFixed(2));
  }
  return product.precoAtual;
}

function computeDiscount(product: CatalogProductPayload, originalPrice: number): number {
  if (typeof product.desconto === "number" && product.desconto >= 0) {
    return product.desconto;
  }
  if (originalPrice <= 0 || originalPrice <= product.precoAtual) {
    return 0;
  }
  return Math.max(0, Math.round(((originalPrice - product.precoAtual) / originalPrice) * 100));
}

export function normalizeCatalogProduct(product: CatalogProductPayload, index: number): Product {
  const precoAtual = typeof product.precoAtual === "number" ? product.precoAtual : 0;
  const precoOriginal = computeOriginalPrice(product);
  const desconto = computeDiscount(product, precoOriginal);
  return {
    id: Number(product.id) || index + 1,
    nome: product.nome,
    precoAtual,
    precoOriginal,
    desconto,
    avaliacao: typeof product.avaliacao === "number" ? product.avaliacao : 4.8,
    quantidadeAvaliacoes: toReviewCount(product.quantidadeAvaliacoes),
    imagem: product.imagem,
    categoria: toCategoryName(product),
    loja: toStore(product.loja),
    marca: normalizeMarca(product),
    linkAfiliado: product.linkAfiliado,
    vendidos: typeof product.vendidos === "number" ? product.vendidos : 0,
    maisVendido: Boolean(product.maisVendido || (typeof product.vendidos === "number" && product.vendidos > 0)),
    destaque: Boolean(product.destaque),
  };
}

export function normalizeCatalogPayload(payload: CatalogPayload | null | undefined): Product[] {
  if (!payload?.products?.length) {
    return EMPTY_PRODUCTS;
  }
  return payload.products.map(normalizeCatalogProduct);
}

export function normalizeRecentVideoPayload(
  payload: RecentVideoPayload | null | undefined,
): RecentVideo[] {
  if (!payload?.items?.length) {
    return EMPTY_RECENT_VIDEOS;
  }
  return payload.items
    .filter((item) => item.videoUrl && item.affiliateLink)
    .map((item) => ({
      assetId: Number(item.assetId) || 0,
      source: item.source || "telegram",
      videoId: Number(item.videoId) || 0,
      postId: item.postId ?? null,
      postedAt: item.postedAt ?? null,
      title: item.title,
      affiliateLink: item.affiliateLink,
      productUrl: item.productUrl ?? null,
      videoUrl: item.videoUrl,
      thumbnailUrl: item.thumbnailUrl ?? null,
      currentPrice: typeof item.currentPrice === "number" ? item.currentPrice : null,
      originalPrice: typeof item.originalPrice === "number" ? item.originalPrice : null,
      discountPercent: typeof item.discountPercent === "number" ? item.discountPercent : 0,
      rating: typeof item.rating === "number" ? item.rating : null,
      group: item.group || "fashion",
    }));
}

export function deriveCategories(products: Product[]): string[] {
  const categories = Array.from(new Set(products.map((product) => product.categoria).filter(Boolean)));
  return categories.length ? categories : [...DEFAULT_CATEGORIES];
}

export function deriveStores(products: Product[]): string[] {
  const stores = Array.from(new Set(products.map((product) => product.loja).filter(Boolean)));
  return stores.length ? stores : [...DEFAULT_STORES];
}

/** Retorna marcas únicas presentes no catálogo. Array vazio = sem dados de marca. */
export function deriveMarcas(products: Product[]): string[] {
  return Array.from(
    new Set(products.map((p) => p.marca).filter((m): m is string => Boolean(m)))
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));
}
